import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { API_URL as API_BASE } from "../../config";

const DEFS_API = `${API_BASE}/planner/group-definitions`;

export default function GroupDefinitionsDialog({ open, onClose, definitions, onRefresh }) {
    const [form, setForm] = useState({ group_key: "", label: "", description: "", color: "#1E88E5" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const resetForm = () => setForm({ group_key: "", label: "", description: "", color: "#1E88E5" });

    const handleAdd = async () => {
        const label = form.label.trim();
        const group_key = (form.group_key.trim() || label.toLowerCase().replace(/[^a-z0-9]+/g, "_")).replace(/^_|_$/g, "");
        if (!label) {
            setError("Label is required.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            await axios.post(DEFS_API, { ...form, group_key, label });
            resetForm();
            await onRefresh?.();
        } catch (e) {
            setError(e?.response?.data?.error || "Failed to add group type.");
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (id) => {
        setBusy(true);
        try {
            await axios.delete(`${DEFS_API}/${id}`);
            await onRefresh?.();
        } catch (e) {
            setError(e?.response?.data?.error || "Delete failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Planner group templates</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define reusable work packages (robot cell, palletizing, software, etc.). New quotes seed from active templates.
                </Typography>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                ) : null}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        label="Label"
                        size="small"
                        value={form.label}
                        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Key (optional)"
                        size="small"
                        value={form.group_key}
                        onChange={(e) => setForm((f) => ({ ...f, group_key: e.target.value }))}
                        sx={{ width: { sm: 160 } }}
                    />
                    <TextField
                        label="Color"
                        size="small"
                        type="color"
                        value={form.color}
                        onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                        sx={{ width: 72 }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={busy}>
                        Add
                    </Button>
                </Stack>
                <TextField
                    label="Description (shown as default scope hint)"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    sx={{ mb: 2 }}
                />

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Label</TableCell>
                            <TableCell>Key</TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(definitions || []).map((d) => (
                            <TableRow key={d.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{d.label}</TableCell>
                                <TableCell>
                                    <Typography variant="caption" fontFamily="monospace">
                                        {d.group_key}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: d.color || "#888" }} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="error" onClick={() => handleDelete(d.id)} disabled={busy}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
